export type Language = 'ru' | 'en' | 'uk' | 'es';

export type TranslationMap = Record<string, string>;

const ЯЗЫКИ: Language[] = ['ru', 'en', 'uk', 'es'];

/**
 * Язык при первом заходе — тот, что стоит у браузера.
 *
 * ⚠️ ЭТО ТОЛЬКО ПЕРВЫЙ ЗАХОД, И В ЭТОМ ВЕСЬ СМЫСЛ. Выбор игрока лежит в
 * localStorage и всегда сильнее: нажал ESP — испанский останется и в браузере
 * с русскими настройками. Спрашиваем браузер лишь тогда, когда выбора ещё нет.
 *
 * ⚠️ СМОТРИМ ВЕСЬ СПИСОК navigator.languages, А НЕ ОДИН navigator.language.
 * У человека с настройками «be, ru, en» первой идёт строка, которой у нас нет;
 * проверь мы только её — он получил бы английский вместо родного ему русского.
 *
 * Область отбрасываем: «ru-RU», «es-MX», «en-GB» — это те же четыре языка.
 *
 * ⚠️ НЕ СОВПАЛО НИЧЕГО — АНГЛИЙСКИЙ, А НЕ РУССКИЙ, КАК БЫЛО. Игра
 * международная, и случайному гостю из Польши или Турции английский понятен
 * вероятнее любого другого нашего языка.
 */
function изБраузера(): Language {
  try {
    const свои = navigator.languages;
    const список: readonly string[] =
      свои !== undefined && свои.length > 0 ? свои : [navigator.language];
    for (const метка of список) {
      const код = String(метка).toLowerCase().split('-')[0];
      if ((ЯЗЫКИ as string[]).includes(код)) return код as Language;
    }
  } catch {
    // Ни navigator, ни его языков может не оказаться — например под Node в тестах.
  }
  return 'en';
}

let currentLang: Language = изБраузера();
const listeners: (() => void)[] = [];

try {
  const saved = localStorage.getItem('interfishing_lang') as Language;
  if (saved && (ЯЗЫКИ as string[]).includes(saved)) {
    currentLang = saved;
  }
} catch {}

import { en, es, ru, uk } from './locales/index.js';

const locales: Record<Language, TranslationMap> = {
  ru,
  en,
  uk,
  es,
};

export function setTranslations(lang: Language, dict: TranslationMap) {
  Object.assign(locales[lang], dict);
}

export function setLanguage(lang: Language) {
  if (currentLang === lang) return;
  currentLang = lang;
  try {
    localStorage.setItem('interfishing_lang', lang);
  } catch {}

  for (const listener of listeners) {
    listener();
  }

  // ⚠️ ЗДЕСЬ СТОЯЛА ПЕРЕЗАГРУЗКА СТРАНИЦЫ, И ОНА ЖЕ ЛОМАЛА ПЕРЕКЛЮЧЕНИЕ.
  //
  // Кнопка языка звала перезагрузку И ЭТА ФУНКЦИЯ звала её же — две подряд.
  // Вторая била по недогруженной первой, модуль не доходил до перевода
  // подписей, и выходило худшее из возможного: язык уже сменился, а форма
  // осталась на прежнем. Со стороны — «нажал и ничего, только что-то грузит».
  //
  // Подписчики выше и есть правильный путь: перевод перерисовывается на месте.
}

export function getLanguage(): Language {
  return currentLang;
}

export function onLanguageChange(callback: () => void) {
  listeners.push(callback);
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const dict = locales[currentLang];
  let text = dict[key];
  if (text === undefined) {
    text = locales['ru'][key] ?? key;
  }

  return подставить(text, vars);
}

/** Вставить значения на места {имя}. */
function подставить(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  let out = text;
  for (const [k, v] of Object.entries(vars)) {
    out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
  }
  return out;
}

/**
 * Правила счёта по языкам: «1 рыба», «2 рыбы», «5 рыб».
 *
 * ⚠️ БЕЗ НИХ ТРИ ЯЗЫКА ИЗ ЧЕТЫРЁХ ГОВОРЯТ НЕВЕРНО. У русского и украинского
 * три формы, у английского и испанского две, и границы у них разные: «21 рыба»
 * (одна форма), но «11 рыб» (другая). Склеивать число со словом, как делалось
 * до сих пор, значит писать «1 рыб» и «5 рыба» — мелочь, которая сразу выдаёт,
 * что текст собран, а не написан.
 *
 * ⚠️ СЧИТАЕТ БРАУЗЕР, А НЕ МЫ. Intl.PluralRules знает правила всех языков и
 * не разойдётся с ними при добавлении пятого. Свой самописный выбор формы
 * пришлось бы дописывать под каждый новый язык — и он бы отстал молча.
 */
const правила = new Map<Language, Intl.PluralRules>();

function правило(lang: Language): Intl.PluralRules {
  let р = правила.get(lang);
  if (р === undefined) {
    р = new Intl.PluralRules(lang);
    правила.set(lang, р);
  }
  return р;
}

/**
 * Перевод с числом. Ключи в словаре — `имя_one`, `имя_few`, `имя_many`,
 * `имя_other`; само число подставляется на место `{n}`.
 *
 * ⚠️ ОТКАТ ИДЁТ ДО `_other`, А НЕ ДО САМОГО КЛЮЧА. Нет нужной формы — берём
 * общую, нет и её — русскую. Показать игроку «keepnet.fish_few» вместо числа
 * рыб хуже, чем показать её с чуть неверным окончанием.
 */
export function tn(key: string, n: number, vars?: Record<string, string | number>): string {
  const форма = правило(currentLang).select(n);
  const свой = locales[currentLang];
  const текст =
    свой[`${key}_${форма}`] ??
    свой[`${key}_other`] ??
    locales.ru[`${key}_${форма}`] ??
    locales.ru[`${key}_other`];
  if (текст === undefined) return key;
  return подставить(текст, { n, ...vars });
}

export const setLang = setLanguage;
